import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NETWORK_BSC, NETWORK_FUJI } from "../../constants";
import { useActiveWeb3React } from "../../hooks/useActiveWeb3React";
import useDebounce from "../../hooks/useDebounce";
import useIsWindowVisible from "../../hooks/useIsWindowVisible";
import { updateBlockNumber } from "./actions";
import { useGetMainNetwork, useSwitchNetwork } from "./hooks";
import Web3 from "web3";
import RPC from "../../config/rpc";
import { chain } from "lodash";

export default function Updater(): null {
  const { library, chainId: network, account } = useActiveWeb3React();
  const mainNetwork = useGetMainNetwork();

  const chainId = account ? network : mainNetwork;

  const dispatch = useDispatch();

  const windowVisible = useIsWindowVisible();

  const [state, setState] = useState<{
    chainId: number | undefined;
    blockNumber: number | null;
  }>({
    chainId,
    blockNumber: null,
  });

  const blockNumberCallback = useCallback(
    (blockNumber: number) => {
      setState((state) => {
        if (chainId === state.chainId) {
          if (typeof state.blockNumber !== "number")
            return { chainId, blockNumber };
          return {
            chainId,
            blockNumber: Math.max(blockNumber, state.blockNumber),
          };
        }
        return state;
      });
    },
    [chainId, setState]
  );

  const switchNetwork = useSwitchNetwork();

  useEffect(() => {
    switchNetwork(account ? chainId : NETWORK_FUJI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network, account]);

  useEffect(() => {
    if (!library || !chainId || !windowVisible) return undefined;

    setState({ chainId, blockNumber: null });
    const web3 = new Web3(RPC[chainId]);

    const getBlock = account ? library : web3.eth;
    const getBlockData = () => {
      getBlock
        .getBlockNumber()
        .then(blockNumberCallback)
        .catch((error) =>
          console.error(
            `Failed to get block number for chainId: ${chainId}`,
            error
          )
        );
    };

    getBlockData();
    var timeInterval;

    if (account) {
      clearInterval(timeInterval);
      library.on("block", blockNumberCallback);
    } else {
      library.removeListener("block", blockNumberCallback);
      timeInterval = setInterval(() => {
        getBlockData();
      }, 10000);
    }
    return () => {
      library.removeListener("block", blockNumberCallback);
      clearInterval(timeInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    chainId,
    network,
    account,
    library,
    blockNumberCallback,
    windowVisible,
  ]);

  const debouncedState = useDebounce(state, 100);

  useEffect(() => {
    if (
      !debouncedState.chainId ||
      !debouncedState.blockNumber ||
      !windowVisible
    )
      return;

    dispatch(
      updateBlockNumber({
        chainId: debouncedState.chainId,
        blockNumber: debouncedState.blockNumber,
      })
    );
  }, [
    windowVisible,
    dispatch,
    debouncedState.blockNumber,
    debouncedState.chainId,
  ]);

  return null;
}
